import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { successHandler } from '../../../../utils/handlers';
import { currencyFormatter, currencyFormatterInShort } from '../../utils/dashboardConstants';

const noOfXAxisDataPoints = (data) => {
  const series = data.series[0];
  const seriesData = series ? series.data : [];
  return seriesData.length;
};

const isChartValue = (value) => typeof value === 'number' && Number.isFinite(value);
const getAxisTitle = (text) => ({ text: text || '' });

const getChartSeries = (series) => {
  if (!Array.isArray(series)) return [];

  return series
    .filter(Boolean)
    .map((item) => ({
      ...item,
      data: Array.isArray(item.data) ? item.data.filter(isChartValue) : [],
    }))
    .filter((item) => item.data.length > 0);
};

const getNumericSeriesValues = (series) => {
  return series.flatMap((item) => item.data).filter(isChartValue);
};

function BarGraph({ id, data }) {
  const series = getChartSeries(data.series);
  const numericValues = getNumericSeriesValues(series);
  let min = numericValues.length ? _.min(numericValues) : 0;
  let max = numericValues.length ? _.max(numericValues) : 0;
  if (min < 0 && max > 0) {
    max *= 1.2;
    min *= 1.2;
  }
  const noOfXAxis = noOfXAxisDataPoints({ ...data, series });

  const chartOptions = {
    chart: {
      type: data.graphType,
      toolbar: {
        show: true,
        export: {
          csv: {
            filename: data.title,
          },
          svg: {
            filename: data.title,
          },
          png: {
            filename: data.title,
          },
        },
      },
      selection: { enabled: false },
    },
    states: {
      normal: {
        filter: { type: 'none', value: 0 },
      },
      hover: {
        filter: { type: 'lighten', value: 0.1 },
      },
      active: {
        filter: { type: 'none', value: 0 },
        allowMultipleDataPointsSelection: false,
      },
    },
    stroke: {
      width: 5,
      colors: ['transparent'],
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: series.length > 2 && noOfXAxis > 3 ? '95%' : '75%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors: data.graphColor,
    dataLabels: {
      enabled: data.showDataLabels || false,
      style: {
        colors: ['#000'],
        fontSize: data.smallDataLabels ? '8px' : '11px',
      },
      offsetY: -20,
      formatter: (value) => {
        return currencyFormatterInShort(value, true);
      },
    },
    xaxis: {
      categories: Array.isArray(data.xLabels) ? data.xLabels : [],
      title: getAxisTitle(data?.graphTitle?.x),
      labels: {
        rotate: 0,
        style: {
          fontWeight: 700,
        },
        formatter: (value) => {
          const label = String(value ?? '');
          return label.length > 10 ? label.split(' ') : label;
        },
      },
    },
    legend: {
      show: data.legendRequired || false,
    },
    grid: {
      show: false,
      borderColor: '#90A4AE',
      strokeDashArray: 5,
      position: 'back',
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    yaxis: [
      {
        name: 'mainYAxis',
        tickAmount: 5,
        max: max + max / 5,
        forceNiceScale: true,
        title: getAxisTitle(data?.graphMode !== 'Combo' ? data?.graphTitle?.y : ''),
        axisTicks: {
          show: true,
        },
        labels: {
          show: true,
          align: 'right',
          style: {
            fontSize: '12px',
            fontWeight: 700,
          },
          offsetY: -1,
          offsetX: -5,
          formatter: (value) => {
            return currencyFormatterInShort(value, true);
          },
        },
      },
    ],
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter(val) {
          return currencyFormatter(val);
        },
      },
    },
  };

  if (min < 0) chartOptions.yaxis[0].min = min + min / 5;

  const handleDownloadMenu = (event) => {
    const parent = document.getElementById(id);
    if (parent) {
      const child = parent.querySelector('.apexcharts-toolbar');
      if (child && !child.contains(event.target)) {
        const menu = parent.querySelector('.apexcharts-menu');
        menu?.classList.remove('apexcharts-menu-open');
      }
      const svgDownload = parent.querySelector('.exportSVG');
      const pngDownload = parent.querySelector('.exportPNG');
      // Handle download popup notification
      if (svgDownload === event.target || pngDownload === event.target) {
        successHandler({
          message: {
            message: 'Graph is exported successfully',
            title: 'Success',
          },
        });
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDownloadMenu);
    return () => {
      document.removeEventListener('click', handleDownloadMenu);
    };
  }, [id]);

  return (
    <div className="bar-graph-continer" id={id}>
      <ReactApexChart options={chartOptions} series={series} type={data.graphType} height={300} />
    </div>
  );
}

BarGraph.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    graphType: PropTypes.string.isRequired,
    graphMode: PropTypes.string.isRequired,
    graphColor: PropTypes.shape([]).isRequired,
    graphTitle: PropTypes.shape({
      x: PropTypes.string,
      y: PropTypes.string,
    }).isRequired,
    series: PropTypes.shape([]).isRequired,
    xLabels: PropTypes.shape([]).isRequired,
    showDataLabels: PropTypes.bool,
    legendRequired: PropTypes.bool,
    smallDataLabels: PropTypes.bool,
  }).isRequired,
};

export default BarGraph;
